import inlineStyles from 'src/index.scss?inline';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { bootstrap } from 'src/bootstrap/bootstrap.ts';
import { ConfigContext } from 'src/configContext';
import { App } from 'src/App';
import { getNumberFormat } from 'src/utils/getNumberFormat.ts';
import { findCurrency } from 'src/utils/findCurrency.ts';
import { AppSensors } from 'src/AppSensors.tsx';
import { AppActuators } from 'src/AppActuators.tsx';
import { Tolgee, TolgeeProvider } from '@tolgee/react';
import { FormatNoBrain } from 'src/utils/tolgeeFormatter.ts';
import { RegionsEnum } from 'src/generated/operations.ts';
import { createGetComparator } from 'src/createGetComparator.ts';
import { ATTRIBUTE_NS } from 'src/bootstrap/constants.ts';
import { RuntimeContextProvider } from 'src/RuntimeContextProvider.tsx';
import { DatabaseProvider } from 'src/database/DatabaseProvider.tsx';
import { createGaPush } from 'src/utils/createGaPush.ts';
import { ProjectsContext } from 'src/projects/ProjectsContext.ts';
import { ProjectsRepository } from 'src/projects/ProjectsRepository.ts';
import { AppTypes } from 'src/types/AppTypes.ts';
import Modal from 'react-modal';

function attachStyleToShadowDom(shadowWrapper: ShadowRoot, cssContent: string) {
    const style = document.createElement('style');

    style.textContent = cssContent;

    shadowWrapper.appendChild(style);
}

export function createShadowRoot(root: Element, styles: string) {
    const shadowRoot = root.attachShadow({ mode: 'open' });
    root.appendChild(shadowRoot);
    const rootIntoShadow = document.createElement('div');
    rootIntoShadow.classList.add('vss-root');
    shadowRoot.appendChild(rootIntoShadow);
    attachStyleToShadowDom(shadowRoot, styles);

    Modal.setAppElement(rootIntoShadow);

    return rootIntoShadow;
}

export async function init({ uri, imageProxyUri, root, sentryHost, cdn, app = 'valves' }: {
    cdn: string;
    sentryHost: string;
    uri: string;
    imageProxyUri: string;
    root: string;
    // @TODO cleanup prefix and baseURL
    prefix: string;
    baseUrl?: string;
    app?: AppTypes;
}) {
    const {
        userSettings,
        messages,
        offlineDatabase,
        databaseCache,
        projectsStore,
    } = await bootstrap(sentryHost, uri);

    const currency = findCurrency(window);

    const rootElement = document.querySelector(root);
    if (rootElement === null) {
        throw new Error('not root element found');
    }
    rootElement.className = '';
    const shadowRoot = createShadowRoot(rootElement, inlineStyles);

    const projectsRepository = new ProjectsRepository(projectsStore, userSettings.currentRegion);
    await projectsRepository.init();
    const getProjectsRepository = (): ProjectsRepository => projectsRepository;

    const staticData = [
        { language: messages.language, namespace: '', data: messages.messages },
        { language: messages.language, namespace: ATTRIBUTE_NS, data: messages.attributes },
    ];

    const tolgee = Tolgee()
        .use(FormatNoBrain())
        .init({
            observerOptions: {
                targetElement: shadowRoot,
            },
            ns: ['', ATTRIBUTE_NS],
            language: messages.language,
            staticData,
        });

    ReactDOM.createRoot(shadowRoot).render(
        <React.StrictMode>
            <TolgeeProvider
                tolgee={tolgee}
                fallback="Toolge Loading..."
            >
                <ProjectsContext.Provider value={getProjectsRepository}>
                    <ConfigContext.Provider value={{
                        gaPush: createGaPush(),
                        getComparator: createGetComparator(userSettings.currentLocale),
                        currency,
                        rootElement: shadowRoot,
                        userSettings,
                        imageProxyUri,
                        uri,
                        mandatoryFilterConfig: {
                            config: [
                                ['company', '7687'],
                                ['shoppingCenter', '2778'],
                            ],
                            values: {
                                company: ['Belimo'],
                                ...(userSettings.shoppingCenter ? {
                                    shoppingCenter: [
                                        userSettings.shoppingCenter,
                                        ...(userSettings.currentRegion === RegionsEnum.Am ? ['SaS_APP'] : []),
                                    ],
                                } : {}),
                            },
                        },
                        numberFormat: getNumberFormat(userSettings.currentLocale),
                    }}
                    >
                        <RuntimeContextProvider>
                            <DatabaseProvider
                                assetDomain={cdn}
                                userSettings={userSettings}
                                databaseCache={databaseCache}
                                offlineDatabase={offlineDatabase}
                                uri={uri}
                            >
                                {app === 'valves' && (<App/>)}
                                {app === 'sensors' && (<AppSensors/>)}
                                {app === 'actuators' && (<AppActuators/>)}
                            </DatabaseProvider>
                        </RuntimeContextProvider>
                    </ConfigContext.Provider>
                </ProjectsContext.Provider>
            </TolgeeProvider>
        </React.StrictMode>,
    );
}

window.vss = {
    init,
};
